import {
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { Store } from '../../../store';
import { TwilioService } from '../../../services/twilio.service';
import { defaultContactsParams, SearchParams } from '../../../data-types/search-params';
import { VoiceContactData, DeviceState } from '../../../data-types/voice-data';
import { AlertConfirmData, defaultAlertConfirmData } from '../../../data-types/alert-data';
import { AlertService } from '../../../services/alert.service';
import { NgSelectComponent } from '@ng-select/ng-select';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatRipple, RippleAnimationConfig } from '@angular/material/core';
import { CallDataType, defaultCallDataToggle } from '../../../state';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MenuService } from '../../../services/menu.service';

@Component({
    selector: 'app-dialpad',
    templateUrl: './dialpad.component.html',
    styleUrls: ['./dialpad.component.scss'],
    animations: [
        trigger('fadeIn', [
            state('void', style({ opacity: '0' })),
            transition('void <=> *', [animate('200ms ease-in')]),
        ]),
        trigger('slideDetails', [
            state(
                'void',
                style({
                    top: '80px',
                    left: '80%',
                    width: 0,
                    height: 0,
                    opacity: '0.6',
                })
            ),
            transition('void => *', [animate('300ms ease-in')]),
            state(
                '*',
                style({
                    top: 0,
                    left: 0,
                    opacity: 1,
                    width: '100%',
                    height: '100%',
                })
            ),
            transition('* => void', [animate('200ms ease-in')]),
        ]),
    ],
})
export class DialpadComponent implements OnInit, OnDestroy {
    @ViewChild('nr_1') nr1: ElementRef;
    @ViewChild('nr_2') nr2: ElementRef;
    @ViewChild('nr_3') nr3: ElementRef;
    @ViewChild('nr_4') nr4: ElementRef;
    @ViewChild('nr_5') nr5: ElementRef;
    @ViewChild('nr_6') nr6: ElementRef;
    @ViewChild('nr_7') nr7: ElementRef;
    @ViewChild('nr_8') nr8: ElementRef;
    @ViewChild('nr_9') nr9: ElementRef;
    @ViewChild('nr_0') nr0: ElementRef;
    @ViewChild('nr_plus') nrPlus: ElementRef;
    @ViewChild('nr_del') nrDel: ElementRef;
    @ViewChild('dial_input') dialInput: NgSelectComponent;
    @ViewChild('outerRipple') outerRipple: MatRipple;
    @ViewChild('innerRipple') innerRipple: MatRipple;

    public contact: VoiceContactData = null;
    public toPhone = '';
    callDetails$ = this.store.select<CallDataType>('call');

    private destroy$ = new Subject();
    private rippleInterval = null;

    @Input() dialerStyle: '' | 'size-xxl' = '';
    @Input() gatherInput = true;

    @HostBinding('class') get hostClass() {
        return this.dialerStyle;
    }

    @HostListener('document:keydown', ['$event'])
    onKeydownHandler(event: KeyboardEvent) {
        // only when on the conversation and not in search field
        if (
            event.metaKey === true ||
            event.ctrlKey === true ||
            event.shiftKey ||
            this.twilioService.isOutboundCallRinging ||
            !this.gatherInput
        ) {
            return;
        }

        if (this.twilioService.isOutboundCallGoingOn) {
            if (+event.key >= 0) {
                this.twilioService.sendDigits(event.key);
            }
            return;
        }

        switch (event.key.toLowerCase()) {
            case '1':
                this.filterPhone(this.nr1.nativeElement as HTMLDivElement, event.key);
                break;
            case '2':
                this.filterPhone(this.nr2.nativeElement as HTMLDivElement, event.key);
                break;
            case '3':
                this.filterPhone(this.nr3.nativeElement as HTMLDivElement, event.key);
                break;
            case '4':
                this.filterPhone(this.nr4.nativeElement as HTMLDivElement, event.key);
                break;
            case '5':
                this.filterPhone(this.nr5.nativeElement as HTMLDivElement, event.key);
                break;
            case '6':
                this.filterPhone(this.nr6.nativeElement as HTMLDivElement, event.key);
                break;
            case '7':
                this.filterPhone(this.nr7.nativeElement as HTMLDivElement, event.key);
                break;
            case '8':
                this.filterPhone(this.nr8.nativeElement as HTMLDivElement, event.key);
                break;
            case '9':
                this.filterPhone(this.nr9.nativeElement as HTMLDivElement, event.key);
                break;
            case '0':
                this.filterPhone(this.nr0.nativeElement as HTMLDivElement, event.key);
                break;
            case '+':
                this.filterPhone(this.nrPlus.nativeElement as HTMLDivElement, event.key);
                break;
            case '#':
                this.filterPhone(this.nrPlus.nativeElement as HTMLDivElement, event.key);
                break;
            case '*':
                this.filterPhone(this.nrPlus.nativeElement as HTMLDivElement, event.key);
                break;
            case 'backspace':
            case 'delete':
                this.filterPhone(this.nrDel.nativeElement as HTMLDivElement, false);
                break;
        }
    }

    @HostListener('document:keyup', ['$event'])
    onKeyupHandler(event: KeyboardEvent) {
        if (this.twilioService.isOutboundCallGoingOn || this.twilioService.isOutboundCallRinging || !this.nr1 || !this.gatherInput) {
            return;
        }
        this.nr1.nativeElement.classList.remove('down');
        this.nr2.nativeElement.classList.remove('down');
        this.nr3.nativeElement.classList.remove('down');
        this.nr4.nativeElement.classList.remove('down');
        this.nr5.nativeElement.classList.remove('down');
        this.nr6.nativeElement.classList.remove('down');
        this.nr7.nativeElement.classList.remove('down');
        this.nr8.nativeElement.classList.remove('down');
        this.nr9.nativeElement.classList.remove('down');
        this.nr0.nativeElement.classList.remove('down');
        this.nrPlus.nativeElement.classList.remove('down');
        this.nrDel.nativeElement.classList.remove('down');
    }

    @HostListener('document:paste', ['$event'])
    pasteHandler(event: ClipboardEvent) {
        if (!this.gatherInput) {
            return;
        }
        event.preventDefault();

        if (event instanceof ClipboardEvent) {
            const paste =
                // @ts-ignore
                '44' + (event.clipboardData || window.clipboardData).getData('text').replace(/^\+44|^44|^0| |-/g, '');
            if (+paste > 0) {
                this.toPhone = paste;
            }
        }
    }

    constructor(
        public store: Store,
        public twilioService: TwilioService,
        private alertService: AlertService,
        private menuService: MenuService
    ) {
        this.twilioService.deviceState$.pipe(takeUntil(this.destroy$)).subscribe((deviceState) => {
            switch (deviceState) {
                case DeviceState.INBOUND:
                    clearInterval(this.rippleInterval);
                    break;
                case DeviceState.INCOMING:
                    clearInterval(this.rippleInterval);
                    this.rippleInterval = setInterval(() => {
                        this.outerRipple?.launch({
                            persistent: false,
                            animation: { enterDuration: 1200, exitDuration: 1000 } as RippleAnimationConfig,
                        });
                        this.innerRipple?.launch({
                            persistent: false,
                            animation: { enterDuration: 1500, exitDuration: 1200 } as RippleAnimationConfig,
                        });
                    }, 1000);
                    break;
                default:
                    clearInterval(this.rippleInterval);
            }
        });
    }

    ngOnInit(): void {}

    sendNumber2Line(numberDom: HTMLDivElement, value: string) {
        numberDom.classList.add('down');
        this.twilioService.sendDigits(value);
    }

    filterPhone(numberDom: HTMLDivElement, value: string | false = null): void {
        if (this.twilioService.isLineBusy) {
            const key = numberDom.innerHTML === '+' ? '*' : numberDom.innerHTML;
            this.sendNumber2Line(numberDom, value === false ? '#' : key);
            return;
        }

        numberDom.classList.add('down');
        // (this.dialInput.searchTerm ? this.dialInput.searchTerm : ''); // somehow not working as expexted
        let filter = this.toPhone;
        if (value !== false) {
            filter += value ? value : numberDom.innerHTML;
        }

        if (value === false) {
            filter = this.toPhone ? this.toPhone.substring(0, this.toPhone.length - 1) : '';
        }

        this.toPhone = filter;
    }

    pickUpInboundCall() {
        if (this.store.selectForLocal('domain')) {
            this.menuService.setDomain(this.twilioService.activeTwilioDomain);
        }
        this.twilioService.pickUpInboundCall();
    }

    doContactSearch(search: { term: string; items: Array<any> }): void {
        this.twilioService.getContacts(
            {
                ...defaultContactsParams,
                ...{ searchText: search.term },
            } as SearchParams,
            search.term
        );
    }

    doPhoneCallAction() {
        if (this.twilioService.disabled || !this.twilioService.isDeviceReady) {
            this.alertService.error(['Voice connection is not ready.']);
            return;
        }

        // the case when we are on the line
        if (this.twilioService.isOutboundCallRinging || this.twilioService.isOutboundCallGoingOn) {
            this.twilioService.hangUpCall();
            this.contact = null;
        } else {
            if (!this.twilioService.isLineBusy) {
                if (this.contact) {
                    this.initiateCallWithConfirmation(this.contact);
                } else {
                    this.alertService.confirm({
                        ...defaultAlertConfirmData,
                        title: 'Please confirm you would like to make a call',
                        message: `Clicking a confirmation button 'Yes', call ${this.toPhone}`,
                        confirmText: 'Yes',
                        confirmCb: this.doTheCall.bind(this, this.toPhone),
                    } as AlertConfirmData);
                }
            }
        }
    }

    private initiateCallWithConfirmation(contact: VoiceContactData): void {
        // for now outbound calls only to the UK making it look nice
        const name = contact.contactFirstname + (contact.contactSurname ? ' ' + contact.contactSurname : '');

        this.alertService.confirm({
            ...defaultAlertConfirmData,
            title: 'Please confirm you would like to call the following staff member',
            message: `Clicking a confirmation button 'Yes', call ${name}`,
            confirmText: 'Yes',
            confirmCb: this.doTheCall.bind(this, contact.contactMobile, contact),
        } as AlertConfirmData);
    }

    doTheCall(toPhone: string, contact?: VoiceContactData): void {
        this.twilioService.startOutboundCall(toPhone, contact ? contact : null);
    }

    toggleOrderDetails() {
        this.store.setProp<CallDataType>('call', {
            ...defaultCallDataToggle,
            showOrderDetails: !this.store.selectForLocal('call').showOrderDetails,
        });
    }

    toggleAvailableAgents() {
        this.store.setProp<CallDataType>('call', {
            ...defaultCallDataToggle,
            showAvailableAgents: !this.store.selectForLocal('call').showAvailableAgents,
        });
    }

    toggleNewCallNote() {
        this.store.setProp<CallDataType>('call', {
            ...defaultCallDataToggle,
            showNewCallNote: !this.store.selectForLocal('call').showNewCallNote,
        });
    }

    toggleCallNotes() {
        this.store.setProp<CallDataType>('call', {
            ...defaultCallDataToggle,
            showCallNotes: !this.store.selectForLocal('call').showCallNotes,
        });
    }

    ngOnDestroy(): void {
        clearInterval(this.rippleInterval);
        this.destroy$.next(true);
    }
}
